import { useCallback, useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { createPortal } from "react-dom";
import { adminHeaders, adminService } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

type FieldType = "text" | "number";
type TagPosition = "left_tag" | "right_upper_tag" | "highlighted_tag" | "custom";
type BannerType = "category" | "seller_deal" | "direct_link";

const BANNER_TYPE_OPTIONS: { value: BannerType; label: string; hint: string }[] = [
  { value: "category", label: "Category only", hint: "Clicking the banner routes to this category's deal feed only." },
  { value: "seller_deal", label: "Seller / Partner deal", hint: "Clicking the banner opens a specific deal from a chosen seller or partner." },
  { value: "direct_link", label: "Direct link", hint: "Clicking the banner opens an admin-provided URL (internal or external)." },
];

interface BannerField {
  key: string;
  label: string;
  type: FieldType;
  position: TagPosition;
  value: string;
}

interface Banner {
  id: string;
  category_slug: string;
  title?: string | null;
  image_url?: string | null;
  fields: BannerField[];
  is_deal_of_day: boolean;
  banner_type?: BannerType;
  seller_id?: string | null;
  seller_name?: string | null;
  seller_email?: string | null;
  seller_role?: string | null;
  deal_id?: string | null;
  deal_collection?: string | null;
  deal_title?: string | null;
  direct_link?: string | null;
  is_active: boolean;
  /** ISO date-time after which the banner stops being served publicly. */
  scheduled_deactivate_at?: string | null;
  /** Derived server-side; "expired" = still is_active but past its schedule. */
  effective_status?: "active" | "inactive" | "expired";
  sort_order: number;
}

/** `datetime-local` wants "YYYY-MM-DDTHH:mm:ss" in *local* time, which
 * `toISOString()` (UTC) does not give. */
const toLocalInputValue = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

interface CategoryOpt {
  slug: string;
  name: string;
  parent_slug?: string | null;
  group_slug?: string | null;
  display_group?: string | null;
  level: number;
}

interface CategoryPickerOption {
  /** value stored as the banner's category_slug */
  value: string;
  label: string;
  kind: "primary" | "sub";
}

interface SellerOpt {
  id: string;
  name: string;
  email?: string;
  role?: string;
}

interface DealOpt {
  id: string;
  collection: string;
  title: string;
  category_slug?: string;
}

const TAG_POSITIONS: { value: TagPosition; label: string; hint: string }[] = [
  { value: "left_tag", label: "Left tag", hint: "Small tag shown on the left side of the banner (e.g. '20% OFF')." },
  { value: "right_upper_tag", label: "Right-upper tag", hint: "Tag shown in the top-right corner of the banner (e.g. 'Limited')." },
  { value: "highlighted_tag", label: "Highlighted tag", hint: "Prominent, emphasized tag drawing attention (e.g. 'Deal of the Day')." },
  { value: "custom", label: "Custom field", hint: "Any other freeform text/number field to attach to this banner." },
];

/** Tooltip rendered into a body-level portal at a fixed screen position so it
 * is never clipped by the scrollable modal container it lives inside. */
function Tooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const show = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCoords({ top: rect.top - 8, left: rect.left + rect.width / 2 });
    setOpen(true);
  };

  return (
    <span className="relative inline-flex items-center ml-1.5 align-middle">
      <button
        type="button"
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onClick={show}
        className="w-4 h-4 rounded-full bg-gray-700 text-gray-300 text-[10px] font-bold flex items-center justify-center hover:bg-indigo-500/40 hover:text-indigo-200"
      >
        i
      </button>
      {open && coords &&
        createPortal(
          <span
            style={{ top: coords.top, left: coords.left, transform: "translate(-50%, -100%)" }}
            className="fixed z-[9999] w-56 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 text-xs p-2 shadow-2xl pointer-events-none"
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

function Label({ children, tip }: { children: React.ReactNode; tip?: string }) {
  return (
    <label className="flex items-center text-xs font-medium text-gray-400 mb-1">
      {children}
      {tip && <Tooltip text={tip} />}
    </label>
  );
}

const emptyField = (): BannerField => ({
  key: `field_${Math.random().toString(36).slice(2, 8)}`,
  label: "",
  type: "text",
  position: "custom",
  value: "",
});

const AdminHomeDealBannersPage: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [bannerCategorySlugs, setBannerCategorySlugs] = useState<string[]>([]);
  const [categories, setCategories] = useState<CategoryOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const { alertModal, showAlert } = useAlertModal();
  const [filterCategory, setFilterCategory] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Banner | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Form state
  const [categorySlug, setCategorySlug] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [isDealOfDay, setIsDealOfDay] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(1);
  const [fields, setFields] = useState<BannerField[]>([emptyField()]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bannerType, setBannerType] = useState<BannerType>("seller_deal");
  const [directLink, setDirectLink] = useState("");

  // Auto-deactivation: the date-time is the source of truth that gets saved;
  // the duration boxes are just a convenient way to compute it as now + D/H/M/S.
  const [deactivateAt, setDeactivateAt] = useState("");
  const [duration, setDuration] = useState({ days: "", hours: "", minutes: "", seconds: "" });

  const applyDuration = (patch: Partial<typeof duration>) => {
    const next = { ...duration, ...patch };
    setDuration(next);
    const totalSeconds =
      (Number(next.days) || 0) * 86400 +
      (Number(next.hours) || 0) * 3600 +
      (Number(next.minutes) || 0) * 60 +
      (Number(next.seconds) || 0);
    // Clearing every duration box clears the schedule; otherwise the date-time
    // is recomputed from "now" so the admin sees exactly when it will expire.
    setDeactivateAt(totalSeconds > 0 ? toLocalInputValue(new Date(Date.now() + totalSeconds * 1000)) : "");
  };

  // Seller/deal picker state
  const [sellerRoleFilter, setSellerRoleFilter] = useState<string>("");
  const [sellerSearch, setSellerSearch] = useState("");
  const [sellerOptions, setSellerOptions] = useState<SellerOpt[]>([]);
  const [sellerDropdownOpen, setSellerDropdownOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<SellerOpt | null>(null);

  const [dealSearch, setDealSearch] = useState("");
  const [dealOptions, setDealOptions] = useState<DealOpt[]>([]);
  const [dealDropdownOpen, setDealDropdownOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<DealOpt | null>(null);

  // "Primary" = the main deal-collection groups (Packages, Cruises, Experiences, Transfers,
  // Stays, Flights, Services, ...) derived from each category's group_slug/display_group —
  // the same grouping typed deals are organized under, not individual leaf categories.
  const primaryGroups = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) {
      const gslug = (c.group_slug || "").trim();
      if (!gslug) continue;
      if (!map.has(gslug)) map.set(gslug, c.display_group || c.name);
    }
    // Fallback for any legacy top-level category that has no group_slug of its own.
    for (const c of categories) {
      if (!c.group_slug && !c.parent_slug && !map.has(c.slug)) map.set(c.slug, c.name);
    }
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [categories]);

  // "Sub" = actual selectable categories that belong to a primary group (excluding the
  // group's own anchor doc, where name === display_group).
  const subCategoryOptions = useMemo(
    () => categories.filter((c) => c.group_slug && c.name !== c.display_group),
    [categories],
  );

  const categoryPickerOptions: CategoryPickerOption[] = useMemo(() => {
    const primaries: CategoryPickerOption[] = primaryGroups.map((g) => ({ value: g.slug, label: g.name, kind: "primary" }));
    const subs: CategoryPickerOption[] = subCategoryOptions.map((c) => {
      const groupLabel = primaryGroups.find((g) => g.slug === c.group_slug)?.name || c.group_slug || "";
      return { value: c.slug, label: groupLabel ? `${groupLabel} / ${c.name}` : c.name, kind: "sub" };
    });
    return [...primaries, ...subs];
  }, [primaryGroups, subCategoryOptions]);

  const filteredCategoryOptions = useMemo(() => {
    if (!categorySearch) return categoryPickerOptions;
    const q = categorySearch.toLowerCase();
    return categoryPickerOptions.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [categoryPickerOptions, categorySearch]);

  const selectedCategoryLabel = useMemo(
    () => categoryPickerOptions.find((o) => o.value === categorySlug)?.label || "",
    [categoryPickerOptions, categorySlug],
  );

  // Filter dropdown only lists categories that actually have banners in the
  // table (not every admin category), so picking a filter never yields an
  // empty result.
  const bannerCategoryOptions = useMemo(() => {
    return bannerCategorySlugs
      .map((slug) => ({ slug, label: categoryPickerOptions.find((o) => o.value === slug)?.label || slug }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bannerCategorySlugs, categoryPickerOptions]);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (filterCategory) params.category_slug = filterCategory;
      setBanners(await adminService.getHomeDealBanners(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  // Unfiltered fetch, kept separate from the (possibly filtered) `banners`
  // list so the "Filter by category" dropdown always lists every category
  // that actually has banners, not just the ones matching the current filter.
  useEffect(() => {
    adminService
      .getHomeDealBanners({})
      .then((all: Banner[]) =>
        setBannerCategorySlugs(Array.from(new Set(all.map((b) => b.category_slug).filter(Boolean)))),
      )
      .catch(() => {});
  }, [banners]);

  useEffect(() => {
    apiClient
      .get<CategoryOpt[]>("/api/admin/v2/categories", { headers: adminHeaders() })
      .then((r) => setCategories(r.data))
      .catch(() => setCategories([]));
  }, []);

  // Seller search (debounced)
  useEffect(() => {
    if (!sellerDropdownOpen) return;
    const t = setTimeout(() => {
      adminService
        .searchHomeDealBannerSellers({ search: sellerSearch || undefined, role: sellerRoleFilter || undefined })
        .then(setSellerOptions)
        .catch(() => setSellerOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [sellerSearch, sellerRoleFilter, sellerDropdownOpen]);

  // Deal search (debounced), scoped to selected seller
  useEffect(() => {
    if (!dealDropdownOpen || !selectedSeller) return;
    const t = setTimeout(() => {
      adminService
        .searchHomeDealBannerSellerDeals(selectedSeller.id, { search: dealSearch || undefined })
        .then(setDealOptions)
        .catch(() => setDealOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [dealSearch, dealDropdownOpen, selectedSeller]);

  const nextSortOrderForCategory = (catSlug: string) => {
    const existing = banners.filter((b) => b.category_slug === catSlug);
    if (!existing.length) return 1;
    return Math.max(...existing.map((b) => b.sort_order || 0)) + 1;
  };

  const resetForm = () => {
    setCategorySlug("");
    setCategorySearch("");
    setTitle("");
    setIsDealOfDay(false);
    setIsActive(true);
    setSortOrder(1);
    setFields([emptyField()]);
    setImageFile(null);
    setImagePreview(null);
    setBannerType("seller_deal");
    setDirectLink("");
    setDeactivateAt("");
    setDuration({ days: "", hours: "", minutes: "", seconds: "" });
    setSelectedSeller(null);
    setSelectedDeal(null);
    setSellerSearch("");
    setDealSearch("");
    setSellerRoleFilter("");
  };

  const openCreate = () => {
    resetForm();
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (banner: Banner) => {
    setEditing(banner);
    setCategorySlug(banner.category_slug || "");
    setCategorySearch("");
    setTitle(banner.title || "");
    setIsDealOfDay(banner.is_deal_of_day);
    setIsActive(banner.is_active);
    setSortOrder(banner.sort_order);
    setFields(banner.fields?.length ? banner.fields : [emptyField()]);
    setImageFile(null);
    setImagePreview(banner.image_url || null);
    setBannerType(banner.banner_type || "seller_deal");
    setDirectLink(banner.direct_link || "");
    setDeactivateAt(banner.scheduled_deactivate_at ? toLocalInputValue(new Date(banner.scheduled_deactivate_at)) : "");
    setDuration({ days: "", hours: "", minutes: "", seconds: "" });
    setSelectedSeller(
      banner.seller_id
        ? { id: banner.seller_id, name: banner.seller_name || banner.seller_id, email: banner.seller_email || undefined, role: banner.seller_role || undefined }
        : null,
    );
    setSelectedDeal(
      banner.deal_id
        ? { id: banner.deal_id, collection: banner.deal_collection || "", title: banner.deal_title || banner.deal_id }
        : null,
    );
    setModalOpen(true);
  };

  const handleImageChange = (file: File | null) => {
    setImageFile(file);
    if (file) setImagePreview(URL.createObjectURL(file));
  };

  const updateField = (idx: number, patch: Partial<BannerField>) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const addField = () => setFields((prev) => [...prev, emptyField()]);
  const removeField = (idx: number) => setFields((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!categorySlug) {
      showAlert("Category is required");
      return;
    }
    let deactivateIso = "";
    if (deactivateAt) {
      const when = new Date(deactivateAt);
      if (Number.isNaN(when.getTime())) {
        showAlert("Auto-deactivate date-time is not a valid date");
        return;
      }
      if (when.getTime() <= Date.now()) {
        showAlert("Auto-deactivate date-time must be in the future");
        return;
      }
      deactivateIso = when.toISOString();
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("category_slug", categorySlug);
      // Always send title/seller/deal/direct_link — even empty — so clearing a
      // field in the edit modal actually clears it on save instead of the
      // previous value silently surviving because the key was omitted.
      fd.append("title", title);
      fd.append("fields", JSON.stringify(fields.filter((f) => f.label.trim())));
      fd.append("is_deal_of_day", String(isDealOfDay));
      fd.append("is_active", String(isActive));
      fd.append("sort_order", String(sortOrder));
      fd.append("banner_type", bannerType);
      fd.append("seller_id", selectedSeller?.id || "");
      fd.append("seller_name", selectedSeller?.name || "");
      fd.append("seller_email", selectedSeller?.email || "");
      fd.append("seller_role", selectedSeller?.role || "");
      fd.append("deal_id", selectedSeller?.role === "partner" ? "" : selectedDeal?.id || "");
      fd.append("direct_link", directLink);
      // An empty form value reads as "not sent" on the backend, so removing a
      // schedule is expressed with an explicit clear flag instead.
      if (deactivateIso) fd.append("scheduled_deactivate_at", deactivateIso);
      else if (editing) fd.append("clear_scheduled_deactivate_at", "true");
      if (imageFile) fd.append("image", imageFile);

      if (editing) {
        await adminService.updateHomeDealBanner(editing.id, fd);
      } else {
        await adminService.createHomeDealBanner(fd);
      }
      setModalOpen(false);
      await fetchBanners();
    } catch (err: any) {
      showAlert(err?.response?.data?.detail || "Failed to save banner");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteHomeDealBanner(deleteTarget.id);
      setDeleteTarget(null);
      await fetchBanners();
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to delete banner");
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (b: Banner) => {
    if (togglingId === b.id) return;
    setTogglingId(b.id);
    try {
      const fd = new FormData();
      fd.append("is_active", String(!b.is_active));
      await adminService.updateHomeDealBanner(b.id, fd);
      await fetchBanners();
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to update banner status");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        className="mb-0"
        title="Home Deal Banners"
        subtitle={
          <>
            Manage category home banners, dynamic tags, and seller/partner deal spotlights. You can add multiple
            banners to the same category — use Sort order (1, 2, 3…) to control which shows first, and toggle
            Active to show/hide a banner without deleting it.
          </>
        }
        actions={
          <>
            <RefreshButton onRefresh={fetchBanners} />
            <button
              onClick={openCreate}
              className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium"
            >
              + New Banner
            </button>
          </>
        }
      />

      <div className="flex items-center gap-3">
        <Label tip="Filter the list below to banners belonging to one category.">Filter by category</Label>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white"
        >
          <option value="">All categories</option>
          {bannerCategoryOptions.map((o) => (
            <option key={o.slug} value={o.slug}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/60 text-gray-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Image</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Title / Tags</th>
              <th className="text-left px-4 py-3">Seller / Deal</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500"><Spinner /></td></tr>
            )}
            {!loading && banners.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No banners yet.</td></tr>
            )}
            {banners
              .slice()
              .sort((a, b) => (a.category_slug === b.category_slug ? a.sort_order - b.sort_order : a.category_slug.localeCompare(b.category_slug)))
              .map((b) => (
              <tr key={b.id} className="hover:bg-gray-800/40">
                <td className="px-4 py-3">
                  {b.image_url ? (
                    <img src={b.image_url} alt="" className="w-16 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-10 rounded bg-gray-800 flex items-center justify-center text-gray-600 text-xs">—</div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-300">{b.category_slug}</td>
                <td className="px-4 py-3 text-gray-400">{b.sort_order}</td>
                <td className="px-4 py-3 text-gray-300">
                  <div>{b.title || <span className="text-gray-600">Untitled</span>}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {b.is_deal_of_day && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px]">Deal of the Day</span>
                    )}
                    {b.fields?.map((f, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 text-[10px]">
                        {f.label}: {f.value}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-400">
                  {b.banner_type === "direct_link" && b.direct_link ? (
                    <div className="truncate max-w-[200px]" title={b.direct_link}>{b.direct_link}</div>
                  ) : b.seller_name ? (
                    <div>
                      <div>{b.seller_name} <span className="text-gray-600">({b.seller_role})</span></div>
                      {b.seller_email && <div className="text-xs text-gray-600">{b.seller_email}</div>}
                      {b.deal_title && <div className="text-xs text-gray-600">{b.deal_title}</div>}
                    </div>
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleActive(b)}
                    disabled={togglingId === b.id}
                    className={`px-2 py-0.5 rounded-full text-xs disabled:opacity-50 disabled:cursor-not-allowed ${b.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-gray-700 text-gray-400"}`}
                  >
                    {togglingId === b.id ? "…" : b.is_active ? "Active" : "Inactive"}
                  </button>
                  {b.effective_status === "expired" && (
                    <div
                      className="mt-1 inline-block px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px]"
                      title={b.scheduled_deactivate_at ? `Auto-deactivated on ${new Date(b.scheduled_deactivate_at).toLocaleString()}` : undefined}
                    >
                      Auto-expired
                    </div>
                  )}
                  {b.effective_status === "active" && b.scheduled_deactivate_at && (
                    <div className="mt-1 text-[10px] text-gray-500">
                      until {new Date(b.scheduled_deactivate_at).toLocaleString()}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => openEdit(b)} className="text-indigo-400 hover:text-indigo-300 text-xs font-medium">Edit</button>
                  <button onClick={() => setDeleteTarget(b)} className="text-red-400 hover:text-red-300 text-xs font-medium">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">{editing ? "Edit Banner" : "New Banner"}</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>

            <div>
              <Label tip="Search by the main group (Packages, Cruises, Experiences, Transfers, Stays, Flights, Services, ...) or by a specific subcategory within one of those groups.">
                Category
              </Label>
              <div className="relative max-w-sm">
                <input
                  value={categorySlug ? selectedCategoryLabel : categorySearch}
                  onChange={(e) => { setCategorySearch(e.target.value); setCategorySlug(""); }}
                  onFocus={() => setCategoryDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setCategoryDropdownOpen(false), 150)}
                  placeholder="Search primary category or subcategory…"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                />
                {categoryDropdownOpen && filteredCategoryOptions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg max-h-56 overflow-y-auto">
                    {filteredCategoryOptions.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        onMouseDown={() => {
                          setCategorySlug(o.value);
                          setCategorySearch("");
                          setCategoryDropdownOpen(false);
                          if (!editing) setSortOrder(nextSortOrderForCategory(o.value));
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 flex items-center justify-between"
                      >
                        <span>{o.label}</span>
                        <span className={`ml-2 text-[10px] uppercase ${o.kind === "primary" ? "text-indigo-400" : "text-gray-500"}`}>
                          {o.kind === "primary" ? "primary" : "sub"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label tip="Internal/admin-facing title for this banner (not necessarily shown to users).">Title</Label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                placeholder="e.g. Summer Getaway Spotlight"
              />
            </div>

            <div>
              <Label tip="Banner image shown on the home page. Recommended wide aspect ratio.">Banner image</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e.target.files?.[0] || null)}
                className="w-full text-sm text-gray-400"
              />
              {imagePreview && <img src={imagePreview} alt="" className="mt-2 w-full max-h-56 object-cover rounded-lg" />}
            </div>

            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={isDealOfDay} onChange={(e) => setIsDealOfDay(e.target.checked)} />
                Deal of the Day
                <Tooltip text="Marks the attached deal as today's featured 'Deal of the Day' for this category/banner." />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                Active
                <Tooltip text="Only Active banners are eligible to be shown. Multiple banners can be active for the same category at once." />
              </label>
              <div className="flex items-center gap-2">
                <Label tip="Controls display order among banners in the same category: 1 shows first, then 2, 3, 4… When multiple banners exist for one category, give each a distinct order.">
                  Sort order
                </Label>
                <input
                  type="number"
                  min={1}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                  className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-sm text-white"
                />
              </div>
            </div>

            {/* Auto-deactivation schedule */}
            <div className="border border-gray-800 rounded-xl p-5 space-y-3">
              <Label tip="Optional. Set how long this banner stays live — after this moment it automatically stops showing on the site. Leave everything empty to keep it live until you switch Active off yourself.">
                Auto-deactivate
              </Label>
              <div className="flex flex-wrap items-end gap-3">
                {([
                  ["days", "Days"],
                  ["hours", "Hours"],
                  ["minutes", "Minutes"],
                  ["seconds", "Seconds"],
                ] as const).map(([key, label]) => (
                  <div key={key}>
                    <div className="text-[11px] text-gray-500 mb-1">{label}</div>
                    <input
                      type="number"
                      min={0}
                      value={duration[key]}
                      onChange={(e) => applyDuration({ [key]: e.target.value })}
                      placeholder="0"
                      className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-sm text-white"
                    />
                  </div>
                ))}
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">Deactivates at</div>
                  <input
                    type="datetime-local"
                    step={1}
                    min={toLocalInputValue(new Date())}
                    value={deactivateAt}
                    onChange={(e) => {
                      setDeactivateAt(e.target.value);
                      // The date-time was edited directly — the duration boxes
                      // no longer describe it, so blank them out.
                      setDuration({ days: "", hours: "", minutes: "", seconds: "" });
                    }}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-sm text-white"
                  />
                </div>
                {deactivateAt && (
                  <button
                    type="button"
                    onClick={() => { setDeactivateAt(""); setDuration({ days: "", hours: "", minutes: "", seconds: "" }); }}
                    className="px-2 py-1 text-xs text-gray-400 hover:text-red-400"
                  >
                    Clear
                  </button>
                )}
              </div>
              {deactivateAt && new Date(deactivateAt).getTime() <= Date.now() && (
                <div className="text-xs text-red-400">Auto-deactivate date-time must be in the future.</div>
              )}
            </div>

            {/* Banner type */}
            <div>
              <Label tip="Choose what happens when this banner is clicked.">Banner type</Label>
              <div className="flex flex-wrap gap-4">
                {BANNER_TYPE_OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-2 text-sm text-gray-300" title={o.hint}>
                    <input
                      type="radio"
                      name="banner_type"
                      checked={bannerType === o.value}
                      onChange={() => setBannerType(o.value)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            {bannerType === "direct_link" && (
              <div>
                <Label tip="URL opened when the banner is clicked — internal app route or external link.">Direct link</Label>
                <input
                  value={directLink}
                  onChange={(e) => setDirectLink(e.target.value)}
                  placeholder="https://… or /deals/category/…"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
            )}

            {/* Seller / Deal picker */}
            {bannerType === "seller_deal" && (
            <div className="border border-gray-800 rounded-xl p-5 space-y-3">
              <Label tip="Attach a specific seller or partner and one of their deals to this banner.">Seller / Partner deal</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <select
                    value={sellerRoleFilter}
                    onChange={(e) => setSellerRoleFilter(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white mb-1"
                  >
                    <option value="">All roles</option>
                    <option value="seller">Seller</option>
                    <option value="partner">Partner</option>
                  </select>
                  <div className="relative">
                    <input
                      value={selectedSeller ? selectedSeller.name : sellerSearch}
                      onChange={(e) => { setSellerSearch(e.target.value); setSelectedSeller(null); setSelectedDeal(null); }}
                      onFocus={() => setSellerDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setSellerDropdownOpen(false), 150)}
                      placeholder="Search seller/partner…"
                      className={`w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white ${selectedSeller ? "pr-8" : ""}`}
                    />
                    {selectedSeller && (
                      <button
                        type="button"
                        onClick={() => { setSelectedSeller(null); setSelectedDeal(null); setSellerSearch(""); }}
                        title="Remove assigned seller/partner"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-400 text-sm leading-none"
                      >
                        ✕
                      </button>
                    )}
                    {sellerDropdownOpen && sellerOptions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg max-h-56 overflow-y-auto">
                        {sellerOptions.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onMouseDown={() => { setSelectedSeller(s); setSellerSearch(""); setSellerDropdownOpen(false); setSelectedDeal(null); setDealSearch(""); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                          >
                            <div>{s.name} <span className="text-gray-500 text-xs">({s.role})</span></div>
                            {s.email && <div className="text-[11px] text-gray-500">{s.email}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {selectedSeller?.role === "partner" ? (
                  <div>
                    <div className="text-[11px] text-gray-500 mb-1 h-[22px] flex items-center">Deal</div>
                    <div className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-500">
                      Partners don't have individual deals — this banner links to their partner page.
                    </div>
                  </div>
                ) : (
                <div>
                  <div className="text-[11px] text-gray-500 mb-1 h-[22px] flex items-center">
                    {selectedSeller ? "Deals for selected seller" : "Select a seller first"}
                  </div>
                  <div className="relative">
                    <input
                      value={selectedDeal ? selectedDeal.title : dealSearch}
                      onChange={(e) => { setDealSearch(e.target.value); setSelectedDeal(null); }}
                      onFocus={() => setDealDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setDealDropdownOpen(false), 150)}
                      disabled={!selectedSeller}
                      placeholder="Search deal…"
                      className={`w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50 ${selectedDeal ? "pr-8" : ""}`}
                    />
                    {selectedDeal && (
                      <button
                        type="button"
                        onClick={() => { setSelectedDeal(null); setDealSearch(""); }}
                        title="Remove assigned deal"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-400 text-sm leading-none"
                      >
                        ✕
                      </button>
                    )}
                    {dealDropdownOpen && dealOptions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg max-h-56 overflow-y-auto">
                        {dealOptions.map((d) => (
                          <button
                            key={`${d.collection}:${d.id}`}
                            type="button"
                            onMouseDown={() => { setSelectedDeal(d); setDealSearch(""); setDealDropdownOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
                          >
                            {d.title} <span className="text-gray-500 text-xs">({d.collection})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
            )}

            {/* Dynamic fields */}
            <div className="border border-gray-800 rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <Label tip="Freeform tags/fields rendered on the banner: choose a type (text/number) and a position (left tag, right-upper tag, highlighted tag, or custom).">
                  Dynamic tag fields
                </Label>
                <button type="button" onClick={addField} className="text-xs text-indigo-400 hover:text-indigo-300">+ Add field</button>
              </div>
              {fields.map((f, idx) => (
                <div key={f.key} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    value={f.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    placeholder="Label (e.g. Discount)"
                    className="col-span-4 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white"
                  />
                  <select
                    value={f.type}
                    onChange={(e) => updateField(idx, { type: e.target.value as FieldType })}
                    className="col-span-2 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                  </select>
                  <select
                    value={f.position}
                    onChange={(e) => updateField(idx, { position: e.target.value as TagPosition })}
                    className="col-span-3 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white"
                    title={TAG_POSITIONS.find((p) => p.value === f.position)?.hint}
                  >
                    {TAG_POSITIONS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  <input
                    value={f.value}
                    onChange={(e) => updateField(idx, { value: e.target.value })}
                    type={f.type === "number" ? "number" : "text"}
                    placeholder="Value"
                    className="col-span-2 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white"
                  />
                  <button type="button" onClick={() => removeField(idx)} className="col-span-1 text-red-400 hover:text-red-300 text-xs">✕</button>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {saving ? "Saving…" : editing ? "Save Changes" : "Create Banner"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-6 space-y-4">
            <p className="text-white text-sm">Delete banner "{deleteTarget.title || deleteTarget.category_slug}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {alertModal}
    </div>
  );
};

export default AdminHomeDealBannersPage;
